var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { P, PCSS, CustomElement, Watch, Utils, PropertyConverters, CustomTypedEvent, CommandEventName, Components, Net, Debounce, avoidHandler, PropertyChangeEvent, PropertyChangeEventName, DeferPromise, Logging, CustomElementUtils } from '@pacem/pacem-core';
import { PacemModelElement } from './types-components';
import { FieldChangeEventName } from './types';
export const FormSubmitEventName = 'submit';
export const FormResetEventName = 'reset';
export class FormSubmitEvent extends CustomTypedEvent {
    constructor(args) {
        super(FormSubmitEventName, args, { bubbles: true, cancelable: true });
    }
}
export class FormResetEvent extends Event {
    constructor() {
        super(FormResetEventName, { bubbles: true, cancelable: true });
    }
}
const EMITTABLE_EVENT_TYPES = ['download' /* < dedicated, for the upload custom element */, FieldChangeEventName /* expose */, CommandEventName];
const SUBMIT_CANCELLATION_TOKEN = false;
export class FormEventEmitter {
    constructor(_element) {
        this._element = _element;
        this._itemEmitHandler = (e) => {
            // handle the actual event first
            this._element.handle(e);
            if (e.type === CommandEventName) {
                // then command-specific event, name being 'item' + command name in lowercase (e.g. 'itemdelete', 'itemselect', ...)
                const evt = e;
                this._element.handle(new CustomEvent('item' + evt.detail.commandName.toLowerCase(), { detail: evt.detail.commandArgument }));
            }
        };
    }
    start() {
        EMITTABLE_EVENT_TYPES.forEach(t => this._element.addEventListener(t, this._itemEmitHandler, false));
    }
    stop() {
        EMITTABLE_EVENT_TYPES.forEach(t => this._element.removeEventListener(t, this._itemEmitHandler, false));
    }
}
export class PacemFormRelevantElement extends Components.PacemElement {
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this.form = CustomElementUtils.findAncestorOfType(this, PacemFormElement);
    }
}
__decorate([
    Watch({ converter: PropertyConverters.Element })
], PacemFormRelevantElement.prototype, "form", void 0);
let PacemFormElement = class PacemFormElement extends PacemFormRelevantElement {
    constructor() {
        super('form');
        this.method = Net.HttpMethod.Post;
        this._emitter = null;
        this._buttons = { submit: undefined, reset: undefined };
        this.#fetching = false;
        this._keyupHandler = (evt) => {
            if (evt.keyCode === /* Enter */ 13
                // no modifiers
                && !evt.shiftKey && !evt.ctrlKey && !evt.altKey && !evt.metaKey) {
                let btn = evt.currentTarget.querySelector(P + '-button[type=submit]');
                if (!Utils.isNull(btn) && Utils.isVisible(btn) && !btn.disabled) {
                    btn.click();
                }
            }
        };
        this._submit = (fetcher, evt) => {
            if (!Utils.isNull(evt)) {
                avoidHandler(evt);
            }
            Promise.all(this._validateAllFields()).then(p => {
                if (p.findIndex(f => f === false) === -1) {
                    this.submit(fetcher).then(_ => {
                        // successful submission here
                        this.setPristine();
                    }, _ => {
                        // catch rejected/canceled
                    });
                }
            });
        };
        this._reset = (evt) => {
            if (!Utils.isNull(evt))
                avoidHandler(evt);
            var resetEvt = new FormResetEvent();
            this.dispatchEvent(resetEvt);
            if (resetEvt.defaultPrevented) {
                return;
            }
            // reset
            // fields first
            for (var field in this._fields) {
                var fld = this._fields[field];
                if (fld instanceof PacemModelElement)
                    fld.reset();
            }
            // then sub-forms
            for (var form of this._subForms) {
                form._reset();
            }
        };
        this._fields = {};
        this._validators = {};
        this._subForms = [];
        this._fieldPropertyChanged = (evt) => {
            switch (evt.detail.propertyName) {
                case 'value':
                    // validate:
                    let model = evt.target;
                    this._checkFieldValidity(model.name);
                    break;
                case 'dirty':
                    this._checkDirtyness();
                    break;
                case 'valid':
                    this._checkValidity();
                    break;
            }
        };
        this._validatorPropertyChanged = (evt) => {
            const validator = evt.target, name = validator.watch;
            if (evt.detail.propertyName === 'invalid') {
                let model = this._fields[validator.watch];
                if (validator.invalid)
                    model.valid = false;
                else
                    this._checkFieldValidity(name);
            }
            else {
                this._checkFieldValidity(name);
            }
        };
        /** Gets or sets whether to hint invalid fields even if still pristine (default 'true').
         * When 'true' - and form is autogenerated - the submit button status is tightly bound to the form valid state. */
        this.suddenValidation = true;
        /** Gets or sets whether the form should show - when autogenerated - the reset button. */
        this.resettable = true;
        this._emitter = new FormEventEmitter(this);
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if ((name === 'method' && this.autogenerate === true && !Utils.isNullOrEmpty(this.metadata)) ||
            (name === 'metadata' && this.autogenerate === true) ||
            (name === 'autogenerate' && val === true && this.metadata && (this.metadata['props'] || this.metadata).length > 0)) {
            this._buildUpForm();
        }
        else {
            switch (name) {
                case 'readonly':
                    //this._lockSubForms(val);
                    break;
            }
        }
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this.form && this.form.registerSubForm(this);
        this._checkValidity();
        this.addEventListener('keyup', this._keyupHandler, false);
        this._emitter.start();
    }
    disconnectedCallback() {
        this._emitter.stop();
        this.removeEventListener('keyup', this._keyupHandler, false);
        this.form && this.form.unregisterSubForm(this);
        super.disconnectedCallback();
    }
    _buildUpForm() {
        const key = '_' + Utils.uniqueCode();
        const uid = this.id = (this.id || key);
        const f_uid = 'fetch' + key;
        //
        const form = document.createElement('form');
        form.id = "form" + key;
        form.className = PCSS + '-form';
        form.setAttribute('pacem', '');
        form.setAttribute('novalidate', '');
        const html = `<${P}-repeater datasource="{{ #${uid}.metadata && (#${uid}.metadata.props || #${uid}.metadata) }}">
    <${P}-panel css="{{ #${uid}.metadata.display && #${uid}.metadata.display.css }}" css-class="{{ (#${uid}.metadata.display && #${uid}.metadata.display.cssClass || []).concat(#${uid}.suddenValidation ? [] : ['lazy-validation']) }}" class="${PCSS}-animatable-list ${PCSS}-list-appear">
        <template>
            <${P}-form-field fetch-credentials="{{ #${uid}.fetchCredentials }}" fetch-headers="{{ #${uid}.fetchHeaders }}"
            logger="{{ #${uid}.logger }}" entity="{{ #${uid}.entity, twoway }}" 
            metadata="{{ ^item }}" readonly="{{ #${uid}.readonly || ^item.isReadOnly }}"></${P}-form-field>
        </template>
    </${P}-panel>
</${P}-repeater>
<${P}-fetch logger="{{ #${uid}.logger }}" id="${f_uid}" method="${this.method}" credentials="{{ #${uid}.fetchCredentials }}" headers="{{ #${uid}.fetchHeaders }}"></${P}-fetch> 
<div class="${PCSS}-buttonset buttons">
    <div class="buttonset-left">
        <${P}-button logger="{{ #${uid}.logger }}" on-click="#${uid}._submit(#${uid}.fetcher || #${f_uid}, $event)" type="submit" hide="{{ #${uid}.readonly || $pacem.isNullOrEmpty(#${uid}.action) || !$pacem.isNull(#${uid}.form) }}"
class="button primary button-size size-small"
css-class="{{ {'buttonset-last': !#${uid}.resettable || !#${uid}.dirty || !$pacem.isNull(#${uid}.form) } }}" 
disabled="{{ (#${uid}.suddenValidation && (!(#${uid}.valid && #${uid}.dirty))) || #${uid}.fetching }}">
            <${P}-panel class="submit-spinner ${PCSS}-anim anim-pop" hide="{{ !#${uid}.fetching }}"><i class="${PCSS}-icon ${PCSS}-anim anim-rotate rotate-uniform display-block">refresh</i></${P}-panel>
            <${P}-text text="{{ #${uid}.submitCaption || 'OK' }}"></${P}-text>
        </${P}-button>
        <${P}-button logger="{{ #${uid}.logger }}" on-click="#${uid}._reset($event)" type="reset" class="button button-size size-small" css-class="{{ {'buttonset-first': #${uid}.readonly || $pacem.isNullOrEmpty(#${uid}.action) || !$pacem.isNull(#${uid}.form)} }}" hide="{{ !#${uid}.resettable || #${uid}.readonly || !#${uid}.dirty || !$pacem.isNull(#${uid}.form) }}" disabled="{{ #${uid}.fetching }}">
            <${P}-text text="{{ #${uid}.resetCaption || 'Reset' }}"></${P}-text>
        </${P}-button>
</div></div>`;
        // buttons are kept hidden if...
        form.innerHTML = html;
        this.innerHTML = '';
        this.appendChild(form);
        form.addEventListener('submit', avoidHandler, false);
        const old = this._buttons;
        const val = this._buttons = {
            submit: form.lastElementChild.firstElementChild.firstElementChild,
            reset: form.lastElementChild.firstElementChild.lastElementChild
        };
        this.dispatchEvent(new PropertyChangeEvent({ propertyName: 'formButtons', currentValue: val, oldValue: old }));
    }
    /** Gets the 'submit' and 'reset' form buttons. */
    get formButtons() {
        return this._buttons;
    }
    #fetching;
    get fetching() {
        return this.#fetching;
    }
    /**
     * Submits the form data via a provided fetcher.
     * @param fetcher The fetching delegate.
     */
    submit(fetcher) {
        return new Promise((resolve, reject) => {
            if (Utils.isNull(fetcher)) {
                reject(`Fetcher cannot be null while submitting a form.`);
            }
            this._submitInternally(fetcher).then(result => {
                this.dispatchEvent(new CustomEvent("success", { detail: result }));
                resolve(result);
            }, err => {
                if (err !== SUBMIT_CANCELLATION_TOKEN) {
                    this.dispatchEvent(new CustomEvent("fail", { detail: err }));
                }
                reject(err);
            });
        });
    }
    /** Resets the form to its pristine values. */
    reset() {
        this._reset();
    }
    _submitInternally(fetcher, args) {
        if (Utils.isNull(fetcher))
            throw `Fetcher cannot be null while submitting a form.`;
        var deferred = DeferPromise.defer();
        const entity = this.entity, entityName = this.entityName;
        var model = {};
        if (!Utils.isNullOrEmpty(entityName)) {
            model[entityName] = entity;
        }
        else {
            model = entity;
        }
        var args = { parameters: model, fields: this._getAllFields() };
        // allow to inject some logic while submitting...
        var submitEvt = new FormSubmitEvent(args);
        this.dispatchEvent(submitEvt);
        if (submitEvt.defaultPrevented) {
            // rejection using `false` identifies a canceled submission.
            deferred.reject(SUBMIT_CANCELLATION_TOKEN);
        }
        else {
            // ...use the output parameters
            fetcher.parameters = Utils.extend({}, submitEvt.detail.parameters);
            // a) fetcher 'success' event listener
            const fnSuccess = (evt) => {
                fetcher.removeEventListener(Net.FetchSuccessEventName, fnSuccess, false);
                fetcher.removeEventListener(Net.FetchErrorEventName, fnError, false);
                const r = evt.detail, empty = Net.Fetcher.isEmpty(r);
                if (empty) {
                    // if no content gets fetched (e.g. '204') then resolve right here (because it won't enter 'fnResult')
                    fetcher.removeEventListener(Net.FetchResultEventName, fnResult, false);
                    this.success = true;
                    deferred.resolve({});
                }
                switch (r.status) {
                    case 205 /*'Reset Content'*/:
                        this._reset();
                        break;
                }
            };
            // b) fetcher 'fetchresult' event listener
            const fnResult = (evt) => {
                // remove listeners (others have already been removed in 'fnSuccess')
                fetcher.removeEventListener(Net.FetchResultEventName, fnResult, false);
                //
                var result = evt.detail;
                if (typeof (result) === 'object' && result.hasOwnProperty('success')) {
                    // DEPRECATED code branch
                    if (result.success === true) {
                        this.success = true;
                        deferred.resolve(result.result || {});
                    }
                    else {
                        this.fail = true;
                        deferred.reject(result.error);
                    }
                }
                else {
                    this.success = true;
                    deferred.resolve(result);
                }
            };
            // c) fetcher 'error' event listener
            const fnError = (evt) => {
                // remove listeners
                fetcher.removeEventListener(Net.FetchSuccessEventName, fnSuccess, false);
                fetcher.removeEventListener(Net.FetchResultEventName, fnResult, false);
                fetcher.removeEventListener(Net.FetchErrorEventName, fnError, false);
                this.fail = true;
                const response = evt.detail;
                deferred.reject(response);
            };
            // fetcher propertychange event listener
            const fnPropChange = (evt) => {
                switch (evt.detail.propertyName) {
                    case 'fetching':
                        if (evt.detail.currentValue === true) {
                            Utils.addClass(this, PCSS + '-fetching');
                            this.#fetching = true;
                            this.dispatchEvent(new PropertyChangeEvent({ currentValue: true, oldValue: false, propertyName: 'fetching' }));
                        }
                        else {
                            this.#fetching = false;
                            this.dispatchEvent(new PropertyChangeEvent({ currentValue: false, oldValue: true, propertyName: 'fetching' }));
                            Utils.removeClass(this, PCSS + '-fetching');
                            fetcher.removeEventListener(PropertyChangeEventName, fnPropChange, false);
                        }
                        break;
                }
            };
            fetcher.addEventListener(PropertyChangeEventName, fnPropChange, false);
            fetcher.addEventListener(Net.FetchSuccessEventName, fnSuccess, false);
            fetcher.addEventListener(Net.FetchResultEventName, fnResult, false);
            fetcher.addEventListener(Net.FetchErrorEventName, fnError, false);
            if (!Utils.isNullOrEmpty(this.action)) {
                fetcher.url = this.action;
            }
            // is it an 'inert' PacemFetchElement?
            if (fetcher instanceof Components.PacemFetchElement && !fetcher.autofetch) {
                fetcher.fetch();
            }
            this.success = this.fail = false;
        }
        //
        return deferred.promise;
    }
    _getAllFields(accumulator = {}) {
        Utils.extend(accumulator, this._fields);
        for (var form of this._subForms) {
            form._getAllFields(accumulator);
        }
        return accumulator;
    }
    /** Modifies the state of the current entity programmatically. */
    apply(state) {
        if (Utils.isNullOrEmpty(state)) {
            return;
        }
        this.entity = Utils.extend({}, this.entity, state);
        for (let name in state) {
            this.setDirty(name);
        }
    }
    /** Sets the current form (or the fields of it having the specified names) in a pristine state. */
    setPristine(...names) {
        this._setAs(false, ...names);
    }
    /** Sets the current form (or the fields of it having the specified names) in a dirty state. */
    setDirty(...names) {
        this._setAs(true, ...names);
    }
    _setAs(dirty, ...names) {
        const hasNames = !Utils.isEmpty(names);
        for (let field in this._fields) {
            if (!hasNames || names.indexOf(field) >= 0) {
                const fld = this._fields[field];
                if (fld instanceof PacemModelElement) {
                    dirty ? fld.setDirty() : fld.setPristine();
                }
            }
        }
        for (let form of this._subForms) {
            dirty ? form.setDirty(...names) : form.setPristine(...names);
        }
    }
    _checkFieldValidity(name) {
        if (!this.suddenValidation) {
            return Promise.resolve(null);
        }
        if (name in this._fields) {
            return this._validateField(name, false);
        }
        return Promise.resolve(false);
    }
    // @Concurrent()
    validate(name) {
        if (!Utils.isNullOrEmpty(name)) {
            return this._validateField(name, false);
        }
        else {
            return Promise.all(this._validateAllFields()).then(b => !b.some(b0 => b0 === false));
        }
    }
    _validateAllFields(setAsDirty = true) {
        const tasks = [];
        for (let field in this._fields) {
            tasks.push(this._validateField(field, setAsDirty));
        }
        for (let sub of this._subForms) {
            Array.prototype.push.apply(tasks, sub._validateAllFields(setAsDirty));
        }
        return tasks;
    }
    // Remove Concurrent from here, better check the closure for params in @Concurrent
    //@Concurrent()
    _validateField(name, setAsDirty) {
        return new Promise((resolve, _) => {
            var model = this._fields[name];
            if (model) {
                var validators = this._validators[model.name];
                if (validators && validators.length > 0) {
                    this.log(Logging.LogLevel.Log, `Computing "${model.name}" validity.`);
                    Promise.all(validators.map(v => v.validate(model.value)
                        .then(_ => !(v.invalid = !_))))
                        .then(results => {
                        let valid = true;
                        for (var result of results) {
                            if (result === false) {
                                valid = false;
                                // set dirty only when not valid
                                if (setAsDirty) {
                                    model.setDirty();
                                }
                                break;
                            }
                        }
                        this.log(Logging.LogLevel.Log, `Property "${model.name}" turns out to be ${(valid ? '' : 'not ')}valid.`);
                        resolve(model.valid = valid);
                    });
                }
                else {
                    this.log(Logging.LogLevel.Log, `Property "${model.name}" has no validators.`);
                    resolve(model.valid = true);
                }
            }
            else {
                this.log(Logging.LogLevel.Log, `Property "${name}" has no fields in this form.`);
                resolve(true);
            }
        });
    }
    // #region FORM HIERARCHY validity/dirtyness
    _checkDirtyness() {
        let dirty = false;
        for (var model in this._fields) {
            if (this._fields[model].dirty === true) {
                dirty = true;
                break;
            }
        }
        if ( /* still */dirty === false) {
            for (var form of this._subForms) {
                if (form.dirty) {
                    dirty = true;
                    break;
                }
            }
        }
        this.dirty = dirty;
    }
    ;
    _checkValidity() {
        let valid = true;
        for (var model in this._fields) {
            this.log(Logging.LogLevel.Log, `Checking "${model}" validity.`);
            if (this._fields[model].valid === false) {
                this.log(Logging.LogLevel.Log, `"${model}" is not valid.`);
                valid = false;
                break;
            }
        }
        if ( /* still */valid === true) {
            for (var form of this._subForms) {
                if (!form.valid) {
                    valid = false;
                    break;
                }
            }
        }
        this.valid = valid;
    }
    ;
    registerSubForm(form) {
        let arr = this._subForms;
        if (arr.indexOf(form) == -1) {
            form.addEventListener(PropertyChangeEventName, this._fieldPropertyChanged, false);
            arr.push(form);
            this._checkValidity();
            this._checkDirtyness();
        }
    }
    unregisterSubForm(form) {
        let arr = this._subForms, ndx = arr.indexOf(form);
        if (ndx >= 0) {
            form.removeEventListener(PropertyChangeEventName, this._fieldPropertyChanged, false);
            arr.splice(ndx, 1);
            this._checkValidity();
            this._checkDirtyness();
        }
    }
    registerField(name, model) {
        var container = this._fields;
        if (container[name] != model) {
            this.unregisterField(name, false);
            container[name] = model;
            this.log(Logging.LogLevel.Log, `Registering "${name}" field.`);
            if (model instanceof HTMLElement) {
                model.addEventListener(PropertyChangeEventName, this._fieldPropertyChanged, false);
            }
            this._checkFieldValidity(name);
            this._checkDirtyness();
        }
    }
    unregisterField(name, check = true) {
        var container = this._fields, current = container[name];
        if (current instanceof HTMLElement) {
            current.removeEventListener(PropertyChangeEventName, this._fieldPropertyChanged, false);
        }
        delete container[name];
        if (current && check === true) {
            this._checkValidity();
            this._checkDirtyness();
        }
    }
    registerValidator(name, validator) {
        var container = this._validators[name] = this._validators[name] || [], index = container.indexOf(validator);
        if (index == -1) {
            container.push(validator);
            if (validator instanceof HTMLElement) {
                validator.addEventListener(PropertyChangeEventName, this._validatorPropertyChanged, false);
            }
            this.log(Logging.LogLevel.Log, `Calling "${name}" validity check after validator registration.`);
            // most likely, at this point, the corresponding field isn't available yet.
            this._checkFieldValidity(name);
        }
    }
    unregisterValidator(name, validator) {
        this._unregisterValidator(name, validator, true);
    }
    _unregisterValidator(name, validator, check) {
        var container = this._validators[name] = this._validators[name] || [], index = container.indexOf(validator);
        if (index >= 0) {
            if (validator instanceof HTMLElement) {
                validator.removeEventListener(PropertyChangeEventName, this._validatorPropertyChanged, false);
            }
            container.splice(index, 1);
            if (check) {
                this._checkFieldValidity(name);
            }
        }
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Element })
], PacemFormElement.prototype, "fetcher", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemFormElement.prototype, "fetchCredentials", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Json })
], PacemFormElement.prototype, "fetchHeaders", void 0);
__decorate([
    Watch({ reflectBack: true, converter: PropertyConverters.String })
], PacemFormElement.prototype, "method", void 0);
__decorate([
    Watch({ reflectBack: true, converter: PropertyConverters.String })
], PacemFormElement.prototype, "submitCaption", void 0);
__decorate([
    Watch({ reflectBack: true, converter: PropertyConverters.String })
], PacemFormElement.prototype, "resetCaption", void 0);
__decorate([
    Debounce(100)
], PacemFormElement.prototype, "_buildUpForm", null);
__decorate([
    Watch({ reflectBack: true, converter: PropertyConverters.Boolean })
], PacemFormElement.prototype, "valid", void 0);
__decorate([
    Watch({ reflectBack: true, converter: PropertyConverters.Boolean })
], PacemFormElement.prototype, "dirty", void 0);
__decorate([
    Watch({ reflectBack: true, converter: PropertyConverters.Boolean })
], PacemFormElement.prototype, "readonly", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemFormElement.prototype, "success", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemFormElement.prototype, "fail", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Json })
], PacemFormElement.prototype, "entity", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemFormElement.prototype, "entityName", void 0);
__decorate([
    Watch()
], PacemFormElement.prototype, "metadata", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Boolean })
], PacemFormElement.prototype, "autogenerate", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemFormElement.prototype, "suddenValidation", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Boolean })
], PacemFormElement.prototype, "resettable", void 0);
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemFormElement.prototype, "action", void 0);
PacemFormElement = __decorate([
    CustomElement({ tagName: P + '-form' })
], PacemFormElement);
export { PacemFormElement };
